import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiPhone, FiMail, FiGift } = FiIcons;

const FreeStrategy = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="strategy" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#5a8f2a] font-semibold text-sm uppercase tracking-widest block mb-4">
              Limited Time Offer
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#4b4b4b] mb-6 tracking-tight leading-tight">
              Get Started With{' '}
              <span className="text-[#5a8f2a]">$1,000 Worth</span> of Advice — For Free
            </h2>
            <p className="text-gray-500 text-lg mb-8 leading-relaxed">
              For a limited time, we are offering a{' '}
              <strong className="text-[#4b4b4b]">30-minute cleaning business strategy session</strong>, with the added bonus of a{' '}
              <strong className="text-[#4b4b4b]">$1,000 marketing analysis</strong> — absolutely FREE.
            </p>

            <div className="bg-[#5a8f2a]/8 border border-[#5a8f2a]/20 rounded-2xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#5a8f2a] flex items-center justify-center flex-shrink-0">
                  <FiGift className="text-white text-xl" />
                </div>
                <div>
                  <p className="font-bold text-[#4b4b4b] mb-1">What's Included (Free):</p>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>✓ 30-minute 1-on-1 strategy session</li>
                    <li>✓ $1,000 marketing analysis of your business</li>
                    <li>✓ Custom roadmap for your cleaning company</li>
                    <li>✓ Competitive analysis for your market</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { icon: FiPhone, label: 'Call Us', value: '(800) 555-1234' },
                { icon: FiMail, label: 'Email Us', value: 'hello@getcleaningclicks.com' },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#5a8f2a]/10 flex items-center justify-center text-[#5a8f2a] flex-shrink-0">
                    <Icon className="text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className="font-semibold text-[#4b4b4b]">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {submitted ? (
              <div className="bg-[#5a8f2a]/10 border border-[#5a8f2a]/30 rounded-3xl p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-[#5a8f2a]/20 flex items-center justify-center mx-auto mb-4">
                  <FiIcons.FiCheck className="text-3xl text-[#5a8f2a]" />
                </div>
                <h3 className="text-2xl font-bold text-[#4b4b4b] mb-2">You're All Set!</h3>
                <p className="text-gray-500">We'll reach out within 24 hours to schedule your free strategy session.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-100 rounded-3xl p-8 shadow-xl shadow-gray-100/50">
                <h3 className="text-xl font-bold text-[#4b4b4b] mb-6">Let's Get Started</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  {[
                    { label: 'Full Name', placeholder: 'Jane Smith', type: 'text' },
                    { label: 'Business Name', placeholder: 'Sparkle Maid Service', type: 'text' },
                    { label: 'Email Address', placeholder: 'jane@yourbusiness.com', type: 'email' },
                    { label: 'Phone Number', placeholder: '(555) 123-4567', type: 'tel' },
                  ].map(({ label, placeholder, type }) => (
                    <div key={label}>
                      <label className="block text-sm font-medium text-gray-600 mb-1.5">{label}</label>
                      <input
                        type={type}
                        required
                        placeholder={placeholder}
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-[#4b4b4b] focus:outline-none focus:border-[#5a8f2a] focus:ring-2 focus:ring-[#5a8f2a]/20 transition-all text-sm"
                      />
                    </div>
                  ))}
                </div>
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">Your Website URL</label>
                  <input
                    type="url"
                    placeholder="https://yourbusiness.com"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-[#4b4b4b] focus:outline-none focus:border-[#5a8f2a] focus:ring-2 focus:ring-[#5a8f2a]/20 transition-all text-sm"
                  />
                </div>
                <div className="mb-6">
                  <label htmlFor="marketing-challenge" className="block text-sm font-medium text-gray-600 mb-1.5">What's Your Biggest Marketing Challenge?</label>
                  <select id="marketing-challenge" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-[#4b4b4b] focus:outline-none focus:border-[#5a8f2a] focus:ring-2 focus:ring-[#5a8f2a]/20 transition-all text-sm">
                    <option>Not enough leads coming in</option>
                    <option>Low website traffic / not ranking on Google</option>
                    <option>Poor Google Ads results</option>
                    <option>Need a new website</option>
                    <option>Need help with Facebook Ads</option>
                    <option>Everything — I need a full strategy</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#5a8f2a] hover:bg-[#4a7522] text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#5a8f2a]/30 text-base"
                >
                  <FiSend /> Claim My Free Strategy Session
                </button>
                <p className="text-center text-gray-400 text-xs mt-4">No commitment required. 100% free. No spam — ever.</p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FreeStrategy;